/**
 * Derivación pura de la pestaña "Estadísticas" del cotizador (paridad con el SSR).
 *
 * Réplica EXACTA de los cálculos del JS `loadDashboardEstadisticas`
 * (`templates/cotizador_puertas.html` de la rama `main`): mismos valores numéricos,
 * partiendo de lo que la API ya devuelve en `CalcResult` + `stats_desktop`. Aquí NO se
 * toca el motor de cálculo (zona prohibida): solo se reordena/agrega lo ya calculado para
 * la presentación. Sin red ni acceso a la API (`docs/frontend_architecture.md`, Pr. 6).
 *
 * Punto fino del SSR: la mano de obra ("Dobleces adicionales", `total_dobles`) sale de
 * `CalcResult`, NO de `stats_desktop`. La descomposición costo/ganancia de cada componente
 * cobrado es `split(comp) = comp/f_iva → { costo: s·f_util, gan: s·(1−f_util) }`.
 */
import type { CalcResult } from "./types";

/** Descomposición de un componente cobrado en costo de material y ganancia. */
export interface ComponentSplit {
  costo: number;
  gan: number;
}

/**
 * Reparte un valor cobrado (con IVA incluido) en costo de material vs ganancia, igual que
 * el `split` del SSR: quita el IVA (`/f_iva`) y aplica el factor de utilidad (`f_util`).
 */
export function splitComponent(comp: number, fIva: number, fUtil: number): ComponentSplit {
  const s = comp / (fIva || 1);
  return { costo: s * fUtil, gan: s * (1 - fUtil) };
}

/** Una categoría de la distribución del dinero cobrado (torta + leyenda). */
export interface DistributionSlice {
  /** Etiqueta de la categoría (leyenda). */
  label: string;
  /** Monto en pesos de la categoría. */
  amount: number;
  /** Porcentaje del total cobrado (sobre `safeTotal`), ya como número (no string). */
  pct: number;
  /** Color de la paleta del frontend para el punto de leyenda y el segmento de torta. */
  color: string;
}

/** Distribución del dinero cobrado: torta (4 segmentos) + porcentajes + margen del centro. */
export interface StatsDistribution {
  slices: DistributionSlice[];
  /** `background` de la torta (`conic-gradient` con segmentos acumulados), réplica del SSR. */
  conicGradient: string;
  /** Margen neto que se muestra en el centro de la torta (`stats_desktop.margen_pct`). */
  margenPct: number;
}

/**
 * Colores de los 4 segmentos de la torta/leyenda. Se mapean a los tokens del frontend
 * (azul de marca para material/IVA-acento queda reservado al gobierno; ganancias en verde
 * `success`; IVA en `warning`), conservando el ORDEN y el significado del SSR.
 */
const SLICE_COLORS = {
  costoMaterial: "#16487A", // info / brand (azul corporativo) — costo de material
  gananciaLamina: "#2f6f4f", // success — ganancia en lámina
  gananciaManoObra: "#4f9b73", // success más claro — ganancia en mano de obra
  iva: "#8a6d1f", // warning — IVA que va al gobierno
} as const;

/**
 * Deriva la distribución del dinero cobrado (réplica de los segmentos de la torta y la
 * leyenda del SSR). Los porcentajes se calculan sobre `safeTotal = max(total, 1)` y los
 * segmentos del `conic-gradient` se acumulan con la misma precisión (`toFixed(2)`).
 */
export function buildStatsDistribution(calc: CalcResult): StatsDistribution {
  const sd = calc.stats_desktop;
  const totalCobrado = Number(calc.total || 0);
  const costoRealLam = Number(sd?.costo_real_lam || 0);
  const gananciaLam = Number(sd?.ganancia_lam || 0);
  const manoObra = Number(calc.total_dobles || 0);
  const ivaTotal = Number(sd?.iva_total || 0);
  const margenPct = Number(sd?.margen_pct || 0);

  const safeTotal = Math.max(totalCobrado, 1);
  const pCosto = Math.max((costoRealLam / safeTotal) * 100, 0);
  const pGanLam = Math.max((gananciaLam / safeTotal) * 100, 0);
  const pMano = Math.max((manoObra / safeTotal) * 100, 0);
  const pIva = Math.max((ivaTotal / safeTotal) * 100, 0);

  const a1 = pCosto.toFixed(2);
  const a2 = (pCosto + pGanLam).toFixed(2);
  const a3 = (pCosto + pGanLam + pMano).toFixed(2);
  const conicGradient =
    `conic-gradient(${SLICE_COLORS.costoMaterial} 0% ${a1}%, ` +
    `${SLICE_COLORS.gananciaLamina} ${a1}% ${a2}%, ` +
    `${SLICE_COLORS.gananciaManoObra} ${a2}% ${a3}%, ` +
    `${SLICE_COLORS.iva} ${a3}% 100%)`;

  return {
    margenPct,
    conicGradient,
    slices: [
      { label: "Costo de material", amount: costoRealLam, pct: pCosto, color: SLICE_COLORS.costoMaterial },
      { label: "Ganancia en lámina", amount: gananciaLam, pct: pGanLam, color: SLICE_COLORS.gananciaLamina },
      {
        label: "Ganancia en mano de obra",
        amount: manoObra,
        pct: pMano,
        color: SLICE_COLORS.gananciaManoObra,
      },
      { label: "IVA que va al gobierno", amount: ivaTotal, pct: pIva, color: SLICE_COLORS.iva },
    ],
  };
}

/** Un paso del desglose "Tu ganancia real después de todo" (+/-/= con su monto). */
export interface ProfitStep {
  sign: "+" | "-" | "=";
  label: string;
  amount: number;
  /** Tono de color para el monto (gobierna la clase Tailwind en la UI). */
  tone: "neutral" | "cost" | "tax" | "profit";
}

/** Bloque "Tu ganancia real después de todo": ganancia neta + margen + 4 pasos. */
export interface ProfitSummary {
  gananciaTotal: number;
  margenPct: number;
  steps: ProfitStep[];
}

/** Deriva el bloque de ganancia neta y sus 4 pasos (réplica del `statsResumenBox` del SSR). */
export function buildProfitSummary(calc: CalcResult): ProfitSummary {
  const sd = calc.stats_desktop;
  const totalCobrado = Number(calc.total || 0);
  const costoRealLam = Number(sd?.costo_real_lam || 0);
  const ivaTotal = Number(sd?.iva_total || 0);
  const gananciaTotal = Number(sd?.ganancia_total || 0);
  const margenPct = Number(sd?.margen_pct || 0);

  return {
    gananciaTotal,
    margenPct,
    steps: [
      { sign: "+", label: "Cobras al cliente", amount: totalCobrado, tone: "neutral" },
      { sign: "-", label: "Pagas al proveedor (lámina)", amount: costoRealLam, tone: "cost" },
      { sign: "-", label: "Pagas al gobierno (IVA)", amount: ivaTotal, tone: "tax" },
      { sign: "=", label: "Te queda en el bolsillo", amount: gananciaTotal, tone: "profit" },
    ],
  };
}

/** Una fila de la tabla "Detalle por componente — de donde viene cada peso". */
export interface ComponentRow {
  /** Concepto (descripción del componente o "TOTAL"). */
  concepto: string;
  /** Cobrado al cliente (con IVA). */
  cobrado: number;
  /** Costo de material. */
  costo: number;
  /** Ganancia ("te queda a ti"). */
  ganancia: number;
  /** Marca la fila TOTAL (estilo destacado). */
  isTotal?: boolean;
}

/**
 * Deriva las filas de la tabla por componente (réplica de `statsTablaModelos` del SSR):
 * cada componente cobrado se descompone con `split`; las filas de refuerzos y tubos solo
 * aparecen si su costo es > 0; "Dobleces adicionales" e "IVA" son casos especiales; cierra
 * con la fila TOTAL. La descomposición usa los MISMOS valores que el SSR.
 */
export function buildComponentRows(calc: CalcResult): ComponentRow[] {
  const sd = calc.stats_desktop;
  const totalCobrado = Number(calc.total || 0);
  const costoRealLam = Number(sd?.costo_real_lam || 0);
  const manoObra = Number(calc.total_dobles || 0);
  const ivaTotal = Number(sd?.iva_total || 0);
  const gananciaTotal = Number(sd?.ganancia_total || 0);
  const fIva = Number(sd?.f_iva || 1.19);
  const fUtil = Number(sd?.f_util || 0.9);
  const subPuerta = Number(sd?.sub_puerta || 0);
  const costoMarco = Number(sd?.costo_marco || 0);
  const costoRef = Number(sd?.costo_refuerzo || 0);
  const costoTubos = Number(sd?.costo_tubos || 0);

  const split = (comp: number) => splitComponent(comp, fIva, fUtil);

  const rows: ComponentRow[] = [];
  const sp = split(subPuerta);
  rows.push({ concepto: "Hoja de la puerta (lámina + dobleces)", cobrado: subPuerta, costo: sp.costo, ganancia: sp.gan });
  const mc = split(costoMarco);
  rows.push({ concepto: "Marco (jambas + cabezal)", cobrado: costoMarco, costo: mc.costo, ganancia: mc.gan });
  if (costoRef > 0) {
    const rf = split(costoRef);
    rows.push({ concepto: "Refuerzos", cobrado: costoRef, costo: rf.costo, ganancia: rf.gan });
  }
  if (costoTubos > 0) {
    const tb = split(costoTubos);
    rows.push({ concepto: "Tubos / viceles", cobrado: costoTubos, costo: tb.costo, ganancia: tb.gan });
  }
  rows.push({ concepto: "Dobleces adicionales (mano de obra pura)", cobrado: manoObra, costo: 0, ganancia: manoObra });
  rows.push({ concepto: "IVA (lo cobras pero se lo pagas al gobierno)", cobrado: ivaTotal, costo: 0, ganancia: 0 });
  rows.push({
    concepto: "TOTAL",
    cobrado: totalCobrado,
    costo: costoRealLam,
    ganancia: gananciaTotal,
    isTotal: true,
  });
  return rows;
}
